import { Box, Flex, Breadcrumb, Button } from '@cowsquare/design';
import { Link } from 'gatsby';
import gql from 'graphql-tag';
import * as R from 'ramda';
import React from 'react';
import { Query } from 'react-apollo';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { format } from 'date-fns/fp';
import { BreadcrumbWrapper } from '../../../BreadcrumbWrapper';
import { HTMLContent } from '../../../HTMLContent';
import { Field, RowLink } from '../../../MedicalContent';
import { Section } from '../../../Section';
import { Title } from '../../../Title';
import PageNotFound from '../../error/404';
import { EMAIL_NEW_LINE } from '../../../../constants/email';
import { useUser } from '../../../../utils/hooks';

const now = format('yyyy/MM/dd', new Date());

const Logo = styled(Box).attrs({
  width: ['72px', '152px'],
  height: ['72px', '152px'],
})`
  background-image: url("${props => props.imgUrl}");
  background-position: center center;
  background-size: cover;
`;

const Info = styled(Flex)`
  ${RowLink} {
    margin-right: ${themeGet('space.4')}px;
  }
`;

const GET_SUPPLIERS_RECORD = gql`
  query getSuppliersRecord($id: uuid!) {
    record: hkdutest_e_drug_suppliers_by_pk(id: $id) {
      id
      name
      fax
      email
      cover {
        link
      }
      content
      address
      phone
      website
      pinned_message {
        content
      }
      drug_enquiry {
        content
      }
      order_form {
        link
      }
      price_list {
        link
      }
      drug_list {
        link
      }
      external_link {
        content
      }
    }
  }
`;

const Content = ({ id }) => {
  const { userInfo } = useUser();
  const userName = R.propOr('', 'name', userInfo);
  const userEmail = R.propOr('', 'email', userInfo);
  const userPhone = R.propOr('', 'phone', userInfo);
  return (
    <Query query={GET_SUPPLIERS_RECORD} variables={{ id }}>
      {({ data, loading }) => {
        if (!loading && !R.prop('record', data)) {
          return <PageNotFound />;
        }

        const record = R.propOr(0, 'record', data);
        const name = R.propOr('', 'name', record);
        const photo = R.pathOr('', ['cover', 'link'], record);
        const email = R.propOr('', 'email', record);
        const pinnedMessageContent = R.pathOr('', ['pinned_message', 'content'], record);
        const drugEnquiryContent = R.pathOr('', ['drug_enquiry', 'content'], record);
        const externalLinkContent = R.pathOr('', ['external_link', 'content'], record);
        const orderFormLink = R.pathOr('', ['order_form', 'link'], record);
        const priceListLink = R.pathOr('', ['price_list', 'link'], record);
        const drugListLink = R.pathOr('', ['drug_list', 'link'], record);
        const orderFormEmailSubject = `HKDU | ${name} Order From Request from Dr. ${userName}`;
        const priceListEmailSubject = `HKDU | ${name} Price List Request from Dr. ${userName}`;
        const salesVisitEmailSubject = `HKDU | ${name} Sales Visit Request from Dr. ${userName}`;
        const orderFormEmailBody = `Hi ${name},${EMAIL_NEW_LINE}${EMAIL_NEW_LINE}I am requesting the order form of your company. Please see the details below:${EMAIL_NEW_LINE}${EMAIL_NEW_LINE}Order form request date: ${now}${EMAIL_NEW_LINE}My contact information:${EMAIL_NEW_LINE}- Name: ${userName}${EMAIL_NEW_LINE}- Email: ${userEmail}${EMAIL_NEW_LINE}- Phone Number: ${userPhone}${EMAIL_NEW_LINE}${EMAIL_NEW_LINE}Inquiry:${EMAIL_NEW_LINE}${EMAIL_NEW_LINE}Thanks.`;
        const priceListEmailBody = `Hi ${name},${EMAIL_NEW_LINE}${EMAIL_NEW_LINE}I am requesting the price list of your company. Please see the details below:${EMAIL_NEW_LINE}${EMAIL_NEW_LINE}Price list request date: ${now}${EMAIL_NEW_LINE}My contact information:${EMAIL_NEW_LINE}- Name: ${userName}${EMAIL_NEW_LINE}- Email: ${userEmail}${EMAIL_NEW_LINE}- Phone Number: ${userPhone}${EMAIL_NEW_LINE}${EMAIL_NEW_LINE}Inquiry:${EMAIL_NEW_LINE}${EMAIL_NEW_LINE}Thanks.`;
        const salesVisitEmailBody = `Hi ${name},${EMAIL_NEW_LINE}${EMAIL_NEW_LINE}I would like to request a sales visit from your company. Please see the details below:${EMAIL_NEW_LINE}${EMAIL_NEW_LINE}Sales visit request date: ${now}${EMAIL_NEW_LINE}My contact information:${EMAIL_NEW_LINE}- Name: ${userName}${EMAIL_NEW_LINE}- Email: ${userEmail}${EMAIL_NEW_LINE}- Phone Number: ${userPhone}${EMAIL_NEW_LINE}${EMAIL_NEW_LINE}Inquiry:${EMAIL_NEW_LINE}${EMAIL_NEW_LINE}Please help to arrange the visit accordingly.${EMAIL_NEW_LINE}Thanks.`;

        return (
          <Section>
            <Helmet>
              <title>{name} - HKDU E-Pharm</title>
            </Helmet>
            <BreadcrumbWrapper>
              <Breadcrumb>
                <Breadcrumb.Item linkProps={{ as: Link, to: '/e-pharm/pharm-dir' }}>
                  Pharm Dir
                </Breadcrumb.Item>
                <Breadcrumb.Item>{name}</Breadcrumb.Item>
              </Breadcrumb>
            </BreadcrumbWrapper>
            <Box mt={[7, 8]} mx={[5, 0]}>
              {photo && <Logo imgUrl={photo} />}
              <Title mt={6} mb={7}>
                {name}
              </Title>
              <Box pb={8}>
                <Field
                  label="E-mail"
                  value={email}
                  render={value => <RowLink href={`mailto:${value}`}>{value}</RowLink>}
                />
                <Field
                  label="Website"
                  value={record.website}
                  render={value => (
                    <RowLink href={value} target="_blank" rel="noopener">
                      {value}
                    </RowLink>
                  )}
                />
                <Field label="Phone" value={record.phone} />
                <Field label="Fax" value={record.fax} />
                <Field label="Address" value={record.address} />
                <Field
                  label="Pinned Message"
                  value={pinnedMessageContent}
                  render={html => <HTMLContent html={html} />}
                />
                {record.content && (
                  <Box pb={8}>
                    <HTMLContent html={record.content} />
                  </Box>
                )}
                <Field
                  label="Drug Enquiry"
                  value={drugEnquiryContent}
                  render={html => <HTMLContent html={html} />}
                />
                <Field
                  label="Order Form"
                  value={orderFormLink || email}
                  render={() => (
                    <Info>
                      <RowLink href={orderFormLink} rel="noopener" target="_blank">
                        <Button variant="primary" disabled={!orderFormLink}>
                          Download
                        </Button>
                      </RowLink>
                      <RowLink
                        href={`mailto:${email}?subject=${orderFormEmailSubject}&body=${orderFormEmailBody}`}
                      >
                        <Button variant="primary" disabled={!email}>
                          Email Order Form
                        </Button>
                      </RowLink>
                    </Info>
                  )}
                />
                <Field
                  label="Price List"
                  value={priceListLink || email}
                  render={() => (
                    <Info>
                      <RowLink href={priceListLink} rel="noopener" target="_blank">
                        <Button variant="primary" disabled={!priceListLink}>
                          Download
                        </Button>
                      </RowLink>
                      <RowLink
                        href={`mailto:${email}?subject=${priceListEmailSubject}&body=${priceListEmailBody}`}
                      >
                        <Button variant="primary" disabled={!email}>
                          Price List Request
                        </Button>
                      </RowLink>
                    </Info>
                  )}
                />
                <Field
                  label="Sales Visit"
                  value={email}
                  render={() => (
                    <RowLink
                      href={`mailto:${email}?subject=${salesVisitEmailSubject}&body=${salesVisitEmailBody}`}
                    >
                      <Button variant="primary" disabled={!email}>
                        Sales Visit Request
                      </Button>
                    </RowLink>
                  )}
                />
                <Field
                  label="Drug List"
                  value={drugListLink}
                  render={() => (
                    <RowLink href={drugListLink} rel="noopener" target="_blank">
                      <Button variant="primary" disabled={!drugListLink}>
                        View List
                      </Button>
                    </RowLink>
                  )}
                />
              </Box>
              <Box pb={8}>
                <Field
                  label="External Link"
                  value={externalLinkContent}
                  render={value => <HTMLContent html={value} />}
                />
              </Box>
            </Box>
          </Section>
        );
      }}
    </Query>
  );
};

export default Content;
